// Considered: DeployReadinessChecklistMolecule — rejected (spec Reuse Audit): its ReadinessItem
// shape has no `state` (pass/open/waived) and no per-item section navigation, so it cannot
// represent a gate check's tri-state or link a blocked check back to the section that owns it.
// Source: type-composer.jsx's footer gate bar (spec § UI Components — PublishGateBarMolecule).
// Publish itself lives in the header (`header.publish`, `type-composer.jsx:832`) — the footer
// only ever shows status and routes to what's blocking it; see `SphereTypeHeaderMolecule`.

import React from 'react';
import { Trans, useTranslation } from 'localization';
import type { GateCheck, GateCheckState } from '../../../api/schemaApi';
import TagAtom, { type TagTone } from '../../atoms/Tag/TagAtom';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import { SkeletonAtom } from '../../atoms/Skeleton';
import MessageAtom from '../../atoms/Message/MessageAtom';
import './PublishGateBarMolecule.scss';

export interface PublishGateBarMoleculeProps {
  /**
   * i18n namespace prefix for every string this bar renders (e.g.
   * `studio.typeEditor.composer.gate`, `studio.arcEditor.footer.gate`) — each caller owns its
   * own copy under its own editor's namespace; the bar itself carries no domain vocabulary.
   */
  namespace: string;
  /**
   * DOM id of the gate-status sentence, so the caller's Publish button can `aria-describedby`
   * it. Each editor names its own (`sphere-type-editor-gate-status`, `arc-editor-gate-status`)
   * — the bar carries no editor's name.
   */
  statusId: string;
  gateStatus: 'idle' | 'loading' | 'ready' | 'error';
  checks: GateCheck[];
  /**
   * A platform-owned type, read-only to this caller (`type-composer.jsx:1911-1927`'s
   * `readOnly` branch). The bar still renders — provenance and the gate chips are shown to
   * everyone — but [Sample record] and the gate status, both of which act on a type the
   * caller may edit, are replaced by the provenance note and the sentence that says why.
   */
  locked?: boolean;
  /** Ghost proposals still awaiting Keep/Discard — holds Publish the same way an open check
   * does, and the gate-status text names whichever fired first (spec `type-composer.jsx:1917-1924`). */
  pendingGhostCount?: number;
  /** A check chip was activated — the caller scrolls/focuses the section it names. */
  onCheckActivate: (sectionId: string) => void;
  /**
   * `[Preview sample record]` — focuses the side rail's Sample tab, per spec `:1912`. Omit for a
   * caller with no sample-record concept (e.g. the arc editor); the button is not rendered.
   */
  onPreviewSample?: () => void;
}

const CHECK_TONE: Record<GateCheckState, TagTone> = {
  pass: 'success',
  open: 'danger',
  waived: 'neutral',
};

const PublishGateBarMolecule: React.FC<PublishGateBarMoleculeProps> = ({
  namespace,
  statusId,
  gateStatus,
  checks,
  locked = false,
  pendingGhostCount = 0,
  onCheckActivate,
  onPreviewSample,
}) => {
  const { t } = useTranslation();
  const openChecks = checks.filter((check) => check.state === 'open');

  return (
    <div className="publish-gate-bar" data-testid="publish-gate-bar" data-region="footer">
      <div className="publish-gate-bar__status">
        {locked ? (
          <>
            {/* `type-composer.jsx:1915` (`t-provenance`) */}
            <span
              className="publish-gate-bar__provenance"
              data-testid="publish-gate-provenance"
              data-datum="footer.platform-owned"
              data-kind="note"
            >
              <i className="pi pi-shield" aria-hidden="true" />
              {t(`${namespace}.platformOwned`)}
            </span>
            {/* `type-composer.jsx:1927` — says why the bar offers nothing to act on. */}
            <span
              className="publish-gate-bar__provenance-note"
              data-testid="publish-gate-provenance-note"
              data-datum="footer.provenance-is-shown-never-edited-sealing"
              data-kind="hint"
            >
              {t(`${namespace}.provenanceNote`)}
            </span>
          </>
        ) : (
          <>
            {onPreviewSample && (
              <ButtonAtom
                variant="secondary"
                size="small"
                className="publish-gate-bar__preview-sample"
                icon="pi pi-eye"
                label={t(`${namespace}.previewSampleRecord`)}
                onClick={onPreviewSample}
                data-testid="publish-gate-preview-sample"
                data-datum="footer.sample-record"
                data-kind="button"
              />
            )}
            {/* `type-composer.jsx:1917-1924` (`t-gate-status`) — always present while editable; the
                bold count names whichever thing is holding Publish, or says there's nothing left. */}
            <span
              id={statusId}
              className="publish-gate-bar__status-text"
              data-testid="publish-gate-status"
              data-datum="footer.hold-proposals"
              data-kind="callout"
            >
              {/* The count lives INSIDE the bold, once — the contract bolds "1 check", not
                  the bare number (`type-composer.jsx:1923`). Rendering `{n}` beside a string
                  that already interpolates `{{count}}` read "1 1 check before publish."; the
                  bold is a Trans component so the count, its noun and the word order all stay
                  in the translation rather than being assembled in JSX. */}
              {pendingGhostCount > 0 ? (
                <Trans
                  i18nKey={`${namespace}.holdProposals`}
                  count={pendingGhostCount}
                  components={{
                    strong: (
                      <strong data-testid="publish-gate-pending-count" data-datum="footer.pending-length" data-kind="strong" />
                    ),
                  }}
                />
              ) : openChecks.length > 0 ? (
                <Trans
                  i18nKey={`${namespace}.checksBeforePublish`}
                  count={openChecks.length}
                  components={{
                    strong: (
                      <strong data-testid="publish-gate-unmet-count" data-datum="footer.check" data-kind="strong" />
                    ),
                  }}
                />
              ) : gateStatus === 'loading' ? (
                t('studio.typeEditor.composer.gate.checkingChecks')
              ) : (
                t(`${namespace}.readyToPublish`)
              )}
            </span>
          </>
        )}
      </div>
      <div className="publish-gate-bar__checks" data-container="footer > list > each(gate)">
        {gateStatus === 'loading' && <SkeletonAtom count={3} width="8rem" height="1.5rem" />}
        {gateStatus === 'error' && (
          <MessageAtom severity="error" text={t(`${namespace}.checkFailed`)} />
        )}
        {gateStatus === 'ready' &&
          checks.map((check) => {
            // `trail-composer.jsx:1929-1936`: `done = g.state === "ok"` (our `pass`) disables the
            // chip and drops its arrow; every other state routes to the section that owns it.
            const done = check.state === 'pass';
            return (
              <TagAtom
                key={check.id}
                label={`${check.label}: ${t(`${namespace}.state.${check.state}`)}`}
                tone={CHECK_TONE[check.state]}
                icon={done ? 'pi-check' : 'pi-circle'}
                trailingIcon={done ? undefined : 'pi-arrow-right'}
                onClick={done ? undefined : () => onCheckActivate(check.sectionId)}
                size="small"
                className="publish-gate-bar__check"
                data-testid={`publish-gate-check-${check.id}`}
                data-datum="footer.g-label"
                data-kind="chip"
                ariaLabel={check.detail ? `${check.label}: ${check.detail}` : check.label}
              />
            );
          })}
      </div>
    </div>
  );
};

PublishGateBarMolecule.displayName = 'PublishGateBarMolecule';

export default PublishGateBarMolecule;
